import React from 'react'
import { useDispatch } from 'react-redux'

import PropTypes from 'prop-types'

import { Button, Modal } from '../../../components/Common'

import { getPimBaseUrl } from '../../../utils/fns'
import { globalSettingsActions } from '../../../utils/slices/settings.slice'
import { reduxBatch } from '@manaflair/redux-batch'
import { sendMsgAction } from '../../../utils/actions'
import { EULA_ACCEPTED_DAY } from '../../../utils/constants'

const EulaModal = ({ show, onHide, appName }) => {
  const dispatch = useDispatch()

  const handleAcceptEula = () => {
    onHide()

    localStorage.setItem(EULA_ACCEPTED_DAY, new Date().toISOString())

    reduxBatch([
      dispatch(globalSettingsActions.updateGlobalSettings({ isEulaAccepted: true })),
      dispatch(
        sendMsgAction({
          description: 'Website Application License Agreement accepted. Thank you.',
        }),
      ),
    ])
  }

  const handleCancelEula = () => {
    onHide()

    setTimeout(() => {
      window.location.replace(getPimBaseUrl(window.location.hostname))
    }, 0)
  }

  return (
    <Modal
      className="-eula"
      show={show}
      onHide={handleCancelEula}
      header={
        <Modal.Header closeButton>
          <h3>{appName}: Website Application License Agreement</h3>
        </Modal.Header>
      }
      footer={
        <Modal.Footer>
          <Button variant="tertiary" onClick={handleCancelEula}>
            <span className="material-icons">close</span>
            <span className="button__text">Do Not Accept</span>
          </Button>

          <Button variant="primary" onClick={handleAcceptEula}>
            <span className="material-icons">check</span>
            <span className="button__text">Accept Application License Agreement</span>
          </Button>
        </Modal.Footer>
      }
    >
      <p>
        <small>Updated 7/11/14</small>
      </p>
      <p>
        PLEASE READ THIS WEBSITE APPLICATION LICENSE AGREEMENT (&#34;AGREEMENT&#34;) BEFORE
        ACCESSING OR USING THE APPLICATION. ACCESSING THE APPLICATION INDICATES YOUR ACCEPTANCE OF
        THE FOLLOWING TERMS AND CONDITIONS. IF YOU DO NOT ACCEPT THE TERMS AND CONDITIONS OF THIS
        AGREEMENT, YOU MUST NOT ACCESS OR USE THIS APPLICATION.
      </p>
      <p>
        IN ADDITION TO THE TERMS AND CONDITIONS EXPLAINED BELOW, WHICH APPLY TO YOUR USE OF THE
        APPLICATION, YOUR USE OF THIS WEB SITE IS GOVERNED BY AND SUBJECT TO THE TERMS AND
        CONDITIONS OF THE{' '}
        <a target="_blank" href="https://www.strongtie.com/user_agreement.html" rel="noreferrer">
          SIMPSON STRONG-TIE USER AGREEMENT
        </a>{' '}
        AND OUR{' '}
        <a target="_blank" href="https://www.strongtie.com/privacy.html" rel="noreferrer">
          PRIVACY POLICY
        </a>
        .
      </p>
      <p>
        PLEASE REFER TO THE COMPANY&#39;S{' '}
        <a
          target="_blank"
          href="https://www.strongtie.com/literature/catalogs.html"
          rel="noreferrer"
        >
          CATALOGS
        </a>{' '}
        AND OTHER INFORMATION AVAILABLE ON THIS WEB SITE FOR ALLOWABLE LOADS, CORRECT COMPONENT
        SPECIFICATIONS, CORRECT FASTENERS, INSTALLATION INSTRUCTIONS, THE TERMS AND CONDITIONS OF
        SALE, BUILDING CODE EVALUATION REPORT LISTINGS AND OTHER IMPORTANT INFORMATION ABOUT THE
        COMPANY&#39;S PRODUCTS. TO OBTAIN A COPY OF THESE CATALOGS OR IF YOU HAVE ANY QUESTIONS,
        PLEASE CONTACT YOUR SIMPSON STRONG-TIE REPRESENTATIVE OR WRITE OR CALL THE COMPANY AT:
      </p>
      <p>
        SIMPSON STRONG-TIE COMPANY INC.
        <br />
        5956 W. LAS POSITAS BLVD.
        <br />
        PLEASANTON, CA 94588
        <br />
        (800) 999-5099
      </p>
      <h4>Limited License</h4>
      <p>
        Subject to the terms and conditions of this Agreement, Simpson Strong-Tie Company Inc.
        (&#34;Company&#34;) grants you a limited, non-exclusive, personal, nontransferable,
        nonsublicensable right and license to access and use the Application. No other right or
        license of any kind is granted by Company to you with respect to the Application.
      </p>
      <h4>Restrictions</h4>
      <p>
        You may not: (1) modify, translate, reverse engineer, decompile, disassemble, or create any
        derivative works based on the Application, or any part thereof, or determine or attempt to
        determine any source code, algorithms, methods or techniques embodied in the Application or
        any part thereof; (2) market, rent or lease the Application for a fee or charge; (3)
        represent that the Application, or any part thereof, is owned by any party other than the
        Company; (4) remove or alter any proprietary notices, labels, marks or identifying
        information of any kind on the Application; (5) incorporate the Application or any part
        thereof into any other application or product; (6) use the Application or any part thereof
        (including, without limitation, Simpson Strong-Tie product names, nomenclature, model
        numbers or any other trademarks) to refer to or facilitate selection of any products other
        than other than Simpson Strong-Tie products; or (7) use the Application for any purpose
        other than in accordance with the terms and conditions of this Agreement.
      </p>

      <p>
        The Company retains all right, title and interest in and to the Application including,
        without limitation, all patent rights, copyrights, trademarks and trade secrets, in and to
        the Application, any part or copy thereof, and any derivative work, regardless of the form
        or media in or on which the original or other copies may subsequently exist. For purposes of
        this Agreement. You agree to take any action reasonably requested by Company to evidence,
        maintain, enforce or defend any of the foregoing rights. You shall not take any action to
        jeopardize, limit or interfere in any manner with Company&#39;s ownership of and rights with
        respect to the Application, or any derivative work. Unauthorized copying or use of the
        Application or any part thereof or failure to comply with the above restrictions will result
        in automatic termination of this license and will make available to Company other legal
        remedies. This license is not a sale of the original or any backup copy. If any of the
        Company&#39;s copyrighted works are reproduced or their contents displayed, you must include
        the legend &#34;Copyright © 2014 Simpson Strong-Tie Company Inc. All rights reserved.&#34;
      </p>

      <h4>No Warranty</h4>

      <p>
        COMPANY MAKES NO WARRANTY WITH RESPECT TO THE APPLICATION. THE APPLICATION IS MADE AVAILABLE
        &#34;AS IS&#34; AND WITH ALL FAULTS. COMPANY DOES NOT WARRANT THAT THE APPLICATION WILL BE
        FREE FROM ERRORS OR THAT ERRORS WILL BE CORRECTED.
      </p>

      <p>
        YOU ACKNOWLEDGE THAT COMPANY DOES NOT HAVE CONTROL OVER YOUR USE OF THE APPLICATION, AND
        COMPANY DOES NOT WARRANT THE PERFORMANCE OR RESULTS THAT MAY BE OBTAINED THROUGH YOUR USE OF
        THE APPLICATION. YOU ASSUME ALL RISKS AND RESPONSIBILITY FOR YOUR USE OF THE APPLICATION.
      </p>

      <p>
        COMPANY MAKES AND YOU RECEIVE NO REPRESENTATIONS, WARRANTIES OR CONDITIONS, EXPRESS,
        IMPLIED, STATUTORY OR ALLEGEDLY EXTENDED IN ANY COMMUNICATION WITH YOU. COMPANY SPECIFICALLY
        DISCLAIMS: (1) ANY IMPLIED WARRANTY OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE,
        WORKMANLIKE EFFORT, ACCURACY, TITLE, QUIET ENJOYMENT, NO ENCUMBRANCES, NO LIENS AND
        NON-INFRINGEMENT; (2) WARRANTIES OR CONDITIONS ARISING THROUGH COURSE OF DEALING OR USAGE OF
        TRADE, AND (3) WARRANTIES OR CONDITIONS THAT ACCESS TO OR OPERATION OF THE APPLICATION WILL
        BE ERROR FREE OR UNINTERRUPTED.
      </p>

      <p>
        THE ABOVE EXCLUSIONS MAY NOT APPLY TO YOU AS SOME STATES DO NOT ALLOW THE EXCLUSION OF
        IMPLIED WARRANTIES. IN ADDITION, YOU MAY ALSO HAVE OTHER RIGHTS WHICH VARY FROM STATE TO
        STATE.
      </p>

      <h4>Disclaimer</h4>

      <p>
        THIS APPLICATION IS A TOOL INTENDED TO BE USED BY TRAINED PROFESSIONALS ONLY. THIS
        APPLICATION IS INTENDED TO BE USED ONLY IN CONJUNCTION WITH COMPANY PRODUCTS. THE
        APPLICATION IS NOT A SUBSTITUTE FOR PROFESSIONAL JUDGEMENT. THIS APPLICATION IS INTENDED TO
        ASSIST WITH PRODUCT SELECTION, AND IS NOT A SUBSTITUTE FOR INDEPENDENT DESIGN OR TESTING FOR
        STRESS, SAFETY AND UTILITY. YOU MUST CONFIRM ALL RESULTS BY REFERRING TO ALL APPLICABLE
        STATE BUILDING CODES, LOCAL AMENDMENTS AND ALL OTHER INFORMATION NECESSARY TO DO SO,
        INCLUDING WITHOUT LIMITATION THE CURRENT SIMPSON STRONG-TIE CATALOG AND WEBSITE. DUE TO THE
        LARGE VARIETY OF POTENTIAL APPLICATIONS FOR THE APPLICATION AND PRODUCTS, COMPANY SHALL NOT
        BE LIABLE IN ANY MANNER WHATSOEVER FOR THE RESULTS OBTAINED THROUGH THE USE OF THE
        APPLICATION. PERSONS USING THE APPLICATION ARE RESPONSIBLE FOR THE SUPERVISION, MANAGEMENT,
        AND CONTROL OF THE APPLICATION. THIS RESPONSIBILITY INCLUDES, BUT IS NOT LIMITED TO, THE
        DETERMINATION OF APPROPRIATE USES FOR THE APPLICATION, FILES, TABLES, DRAWINGS AND
        INFORMATION AND THE SELECTION OF THE OTHER PRODUCTS, SOFTWARE AND MATERIALS TO ACHIEVE
        INTENDED RESULTS. PERSONS USING THE APPLICATION ARE ALSO RESPONSIBLE FOR ESTABLISHING THE
        ADEQUACY OF INDEPENDENT PROCEDURES FOR TESTING THE RELIABILITY AND ACCURACY OF ANY OUTPUT,
        INCLUDING ALL PRODUCTS SELECTED BY USING THE APPLICATION.
      </p>

      <h4>Limitation Of Liability</h4>

      <p>
        IN NO EVENT WILL COMPANY BE LIABLE FOR ANY DAMAGES, INCLUDING LOSS OF DATA OR INFORMATION OF
        ANY KIND, LOSS OF BUSINESS, LOST PROFITS, INTERRUPTION OF BUSINESS, COST OF COVER OR ANY
        OTHER SPECIAL, INCIDENTAL, CONSEQUENTIAL OR INDIRECT DAMAGES ARISING OUT OF THIS AGREEMENT
        OR THE USE OR INABILITY TO USE THE APPLICATION, HOWEVER CAUSED, AND ON ANY THEORY OF
        LIABILITY (WHETHER IN CONTRACT, TORT, INDEMNITY OR OTHERWISE). THIS LIMITATION WILL APPLY
        EVEN IF COMPANY OR ANY DISTRIBUTOR HAS BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGE. YOU
        ACKNOWLEDGE THAT THE ROYALTY-FREE BASIS FOR RECEIVING THIS LICENSE REFLECTS THIS ALLOCATION
        OF RISK. IF YOU OBTAINED THIS LICENSE IN THE UNITED STATES, SOME STATES DO NOT ALLOW THE
        LIMITATION OR EXCLUSION OF LIABILITY FOR INCIDENTAL OR CONSEQUENTIAL DAMAGES, SO THE ABOVE
        LIMITATION MAY NOT APPLY TO YOU.
      </p>

      <h4>Term and Termination</h4>

      <p>
        The license provided herein is effective until terminated. This license automatically
        terminates if you fail to comply with its terms and conditions.
      </p>

      <h4>Effect of Termination</h4>

      <p>
        Termination of this Agreement by Company shall not act as a waiver of any breach of this
        Agreement and shall not release you from any liability for breach of your obligations under
        this Agreement. Company shall not be liable to you for damages of any kind as a result of
        terminating this Agreement in accordance with its terms, and termination of this Agreement
        by Company shall be without prejudice to any other right or remedy of Company under this
        Agreement or applicable law.
      </p>

      <h4>Indemnification</h4>

      <p>
        By accepting this Agreement you agree to defend, indemnify and hold harmless Company, its
        officers, employees, agents, subsidiaries and affiliates from any direct, indirect,
        incidental, special, consequential or exemplary damages arising out of, relating to, or
        resulting from your breach of this Agreement, use of the Application or any act or omission
        by you.
      </p>

      <h4>Assignment</h4>

      <p>
        This Agreement may not be assigned, in whole or part, whether voluntarily, by operation of
        law or otherwise, by you without the prior written consent of Company. Subject to the
        preceding sentence, the rights and liabilities of the parties hereto are binding on, and
        shall inure to the benefit of, the parties and their respective successors and assigns. Any
        attempted assignment other than in accordance with this section shall be null and void.
      </p>

      <h4>Severability</h4>

      <p>
        If the application of any provision of this Agreement to any particular facts or
        circumstances shall be held to be invalid or unenforceable by an arbitration panel or a
        court of competent jurisdiction, then: (a) the validity and enforceability of such provision
        as applied to any other particular facts or circumstances and the validity of other
        provisions of this Agreement shall not in any way be affected or impaired thereby, and (b)
        such provision shall be enforced to the maximum extent possible so as to effect the intent
        of the parties and reformed without further action by the parties to the extent necessary to
        make such provision valid and enforceable.
      </p>

      <h4>Relationship of The Parties</h4>

      <p>
        Nothing contained in this Agreement shall be deemed or construed as creating a joint
        venture, partnership, agency, employment or fiduciary relationship between the parties.
        Neither party nor their agents have any authority of any kind to bind the other party in any
        respect whatsoever, and the relationship of the parties is, and at all times shall continue
        to be, that of independent contractors.
      </p>

      <h4>Force Majeure</h4>

      <p>
        Company shall not be responsible or have any liability for any delay or failure to perform
        to the extent due to unforeseen circumstances or causes beyond its reasonable control,
        including, without limitation, acts of God, earthquake, fire, flood, embargoes, labor
        disputes and strikes, riots, war, novelty of product manufacture or other unanticipated
        product development problems, and acts of civil and military authorities.
      </p>

      <h4>Entire Agreement</h4>

      <p>
        This Agreement constitutes the entire agreement between the parties concerning the subject
        matter hereof and supersedes all prior or contemporaneous representations, discussions,
        proposals, negotiations, conditions, agreements and communications, whether oral or written,
        between the parties relating to the subject matter of this Agreement and all past courses of
        dealing or industry custom. No amendment or modification of any provision of this Agreement
        shall be effective unless in writing and signed by a duly authorized signatory of Company
        and you.
      </p>

      <h4>General</h4>

      <p>
        This agreement shall be governed by the laws of the State of California, including its
        Uniform Commercial Code without reference to conflicts of laws principles. This Agreement is
        the entire agreement between the parties and supersedes any other communications or
        advertising with respect to the Application and accompanying documentation. If you have any
        questions, please contact in writing Simpson Strong-Tie Customer Service.
      </p>

      <h4>Acceptance of Terms</h4>

      <p>
        By using the Application, you agree to the terms and conditions of this Agreement. The
        Application should only be used by experienced designers. As a user, you are responsible for
        assuring the accuracy of your input to the Application. All output should be reviewed for
        accuracy by a qualified design professional before product use. The Application&#39;s
        product selection must include a thorough review of the latest version of the Simpson
        catalog and website to insure appropriate selection and product application.
      </p>

      <p>
        For help in using the Application, refer to the information made available in the
        Application and the Company&#39;s website. For technical engineering support, call (800)
        999-5099. See{' '}
        <a target="_blank" href="https://www.strongtie.com" rel="noreferrer">
          www.strongtie.com
        </a>{' '}
        for latest version.
      </p>
    </Modal>
  )
}

EulaModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  appName: PropTypes.string.isRequired,
}

export default EulaModal
